import React, { forwardRef } from 'react';

const ResumePreview = forwardRef(({ data }, ref) => {
  const { personalInfo, objective, experiences, education, skills, languages } = data;

  const hasContent = personalInfo?.fullName || objective || experiences?.length > 0 || 
    education?.length > 0 || skills?.length > 0 || languages?.length > 0;

  return (
    <div
      ref={ref}
      className="bg-white"
      style={{ 
        width: '794px', 
        minHeight: '1123px', 
        padding: '48px 56px',
        fontFamily: "'Inter', 'Segoe UI', Arial, sans-serif",
        fontSize: '11px',
        lineHeight: '1.5',
        color: '#1f2937'
      }}
    >
      {!hasContent ? (
        <div className="flex items-center justify-center h-full text-gray-400">
          <p className="text-center text-base">
            Preencha seus dados para visualizar<br />o currículo aqui
          </p>
        </div>
      ) : (
        <div>
          {/* Header - Nome e Informações de Contato */}
          <header style={{ marginBottom: '24px', borderBottom: '2px solid #059669', paddingBottom: '16px' }}>
            <h1 style={{ 
              fontSize: '28px', 
              fontWeight: '700', 
              color: '#111827',
              marginBottom: '4px',
              letterSpacing: '-0.5px'
            }}>
              {personalInfo?.fullName || 'Seu Nome'}
            </h1>
            
            {personalInfo?.desiredPosition && (
              <p style={{ 
                fontSize: '16px', 
                color: '#059669', 
                fontWeight: '600',
                marginBottom: '12px'
              }}>
                {personalInfo.desiredPosition}
              </p>
            )}
            
            <div style={{ 
              display: 'flex', 
              flexWrap: 'wrap', 
              gap: '16px',
              fontSize: '12px',
              color: '#4b5563'
            }}>
              {personalInfo?.email && (
                <span>{personalInfo.email}</span>
              )}
              {personalInfo?.phone && (
                <span>{personalInfo.phone}</span>
              )}
              {(personalInfo?.city || personalInfo?.state) && (
                <span>
                  {[personalInfo?.city, personalInfo?.state].filter(Boolean).join(' - ')}
                </span>
              )}
            </div>
          </header>

          {/* Objetivo Profissional */}
          {objective && (
            <section style={{ marginBottom: '20px' }}>
              <h2 style={{ 
                fontSize: '13px', 
                fontWeight: '700', 
                color: '#059669',
                textTransform: 'uppercase',
                letterSpacing: '1px',
                marginBottom: '8px',
                borderBottom: '1px solid #e5e7eb',
                paddingBottom: '4px'
              }}>
                Objetivo Profissional
              </h2>
              <p style={{ 
                fontSize: '12px', 
                color: '#374151',
                lineHeight: '1.6',
                textAlign: 'justify'
              }}>
                {objective}
              </p>
            </section>
          )}

          {/* Experiência Profissional */}
          {experiences && experiences.length > 0 && (
            <section style={{ marginBottom: '20px' }}>
              <h2 style={{ 
                fontSize: '13px', 
                fontWeight: '700', 
                color: '#059669',
                textTransform: 'uppercase',
                letterSpacing: '1px',
                marginBottom: '12px',
                borderBottom: '1px solid #e5e7eb',
                paddingBottom: '4px'
              }}>
                Experiência Profissional
              </h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                {experiences.map((exp) => (
                  <div key={exp.id}>
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'flex-start',
                      marginBottom: '2px'
                    }}>
                      <h3 style={{ 
                        fontSize: '13px', 
                        fontWeight: '600', 
                        color: '#111827'
                      }}>
                        {exp.position}
                      </h3>
                      <span style={{ 
                        fontSize: '11px', 
                        color: '#6b7280',
                        whiteSpace: 'nowrap',
                        marginLeft: '16px'
                      }}>
                        {exp.startDate} - {exp.current ? 'Atual' : exp.endDate}
                      </span>
                    </div>
                    <p style={{ 
                      fontSize: '12px', 
                      color: '#059669',
                      fontWeight: '500',
                      marginBottom: '4px'
                    }}>
                      {exp.company}
                    </p>
                    {exp.description && (
                      <p style={{ 
                        fontSize: '11px', 
                        color: '#4b5563',
                        lineHeight: '1.5'
                      }}>
                        {exp.description}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Formação Acadêmica */}
          {education && education.length > 0 && (
            <section style={{ marginBottom: '20px' }}>
              <h2 style={{ 
                fontSize: '13px', 
                fontWeight: '700', 
                color: '#059669',
                textTransform: 'uppercase',
                letterSpacing: '1px',
                marginBottom: '12px',
                borderBottom: '1px solid #e5e7eb',
                paddingBottom: '4px'
              }}>
                Formação Acadêmica
              </h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {education.map((edu) => (
                  <div key={edu.id}>
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'flex-start',
                      marginBottom: '2px'
                    }}>
                      <h3 style={{ 
                        fontSize: '13px', 
                        fontWeight: '600', 
                        color: '#111827'
                      }}>
                        {edu.course}
                      </h3>
                      <span style={{ 
                        fontSize: '11px', 
                        color: '#6b7280',
                        whiteSpace: 'nowrap',
                        marginLeft: '16px'
                      }}>
                        {edu.startDate} - {edu.current ? 'Cursando' : edu.endDate}
                      </span>
                    </div>
                    <p style={{ 
                      fontSize: '11px', 
                      color: '#4b5563'
                    }}>
                      {edu.degree} • {edu.institution}
                    </p>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Habilidades */}
          {skills && skills.length > 0 && (
            <section style={{ marginBottom: '20px' }}>
              <h2 style={{ 
                fontSize: '13px', 
                fontWeight: '700', 
                color: '#059669',
                textTransform: 'uppercase',
                letterSpacing: '1px',
                marginBottom: '10px',
                borderBottom: '1px solid #e5e7eb',
                paddingBottom: '4px'
              }}>
                Habilidades
              </h2>
              <div style={{ 
                display: 'flex', 
                flexWrap: 'wrap', 
                gap: '8px'
              }}>
                {skills.map((skill) => (
                  <span
                    key={skill.id}
                    style={{
                      fontSize: '11px',
                      color: '#374151',
                      backgroundColor: '#f3f4f6',
                      padding: '4px 10px',
                      borderRadius: '4px'
                    }}
                  >
                    {skill.name}
                    {skill.level && (
                      <span style={{ color: '#6b7280', marginLeft: '4px' }}>
                        • {skill.level}
                      </span>
                    )}
                  </span>
                ))}
              </div>
            </section>
          )}

          {/* Idiomas */}
          {languages && languages.length > 0 && (
            <section>
              <h2 style={{ 
                fontSize: '13px', 
                fontWeight: '700', 
                color: '#059669',
                textTransform: 'uppercase',
                letterSpacing: '1px',
                marginBottom: '10px',
                borderBottom: '1px solid #e5e7eb',
                paddingBottom: '4px'
              }}>
                Idiomas
              </h2>
              <div style={{ 
                display: 'flex', 
                flexWrap: 'wrap', 
                gap: '16px'
              }}>
                {languages.map((lang) => (
                  <span
                    key={lang.id}
                    style={{
                      fontSize: '12px',
                      color: '#374151'
                    }}
                  >
                    <strong>{lang.name}</strong>
                    {lang.level && (
                      <span style={{ color: '#6b7280', marginLeft: '4px' }}>
                        - {lang.level}
                      </span>
                    )}
                  </span>
                ))}
              </div>
            </section>
          )}
        </div>
      )}
    </div>
  );
});

ResumePreview.displayName = 'ResumePreview';

export default ResumePreview;
